import { Badge } from '../../../components/ui';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { ngayVN, soVN } from '../../../lib/dinh-dang';
import { TONG_TOKEN_AI } from '../constants';
import { beRongThanh, hienPhanTram, mucToken, vuotHanMuc } from '../nghiep-vu/han-muc-token';
import type { AiTokenQuota } from '../types';

/** Nhãn trạng thái của mức token: chỉ có khi sắp hết hoặc đã chặn. */
export function NhanMucToken({ q }: { q: AiTokenQuota }) {
  const { t } = useT();
  const muc = mucToken(q);
  if (muc === 'khong-gioi-han') return null;
  const tong = TONG_TOKEN_AI[muc];
  if (!tong.badge || !tong.khoaBadge) return null;
  return <Badge variant={tong.badge}>{t(tong.khoaBadge)}</Badge>;
}

/** "Tháng này: 1.234 đầu vào · 567 đầu ra" — token Fox đã dùng trong tháng chia hai chiều. */
function DongVaoRa({ q }: { q: AiTokenQuota }) {
  const { t, locale } = useT();
  return (
    <p className="text-xs tabular-nums text-content-muted">
      {t('tokenAi.tomThangVaoRa', { vao: soVN(q.month_used_input, locale), ra: soVN(q.month_used_output, locale) })}
    </p>
  );
}

/**
 * Tóm tắt token AI: số còn lại trên tổng khả dụng (mọi túi), thanh phần trăm đã dùng, số đã dùng và ngày túi gói tài
 * nguyên về 0, rồi token đã dùng tháng này chia đầu vào, đầu ra (ADR-0123 mục 1). Chỉ token Fox: không hệ số, không
 * token thật.
 * Dùng chung ở chi tiết tenant (cổng nền tảng) và tổng quan của tổ chức (cổng tenant).
 *
 * Không giới hạn thì không có thanh: thanh không có mẫu số là thanh nói dối.
 */
export function TomTatTokenAi({ q, tranPhanTram = false }: { q: AiTokenQuota; /** Vượt 100% thì hiện `>100%` kèm số token vượt. */ tranPhanTram?: boolean }) {
  const { t, locale } = useT();
  const muc = mucToken(q);
  const ngay = ngayVN(q.resets_at);

  if (muc === 'khong-gioi-han' || q.limit_value === null) {
    return (
      <div className="space-y-1">
        <p className="text-2xl font-semibold tabular-nums text-content-primary">
          {t('hanMucToken.soToken', { so: soVN(q.used, locale) })}
        </p>
        <p className="text-xs text-content-muted">
          {t('hanMucToken.khongGioiHanDong', { ngay })}
        </p>
        <DongVaoRa q={q} />
      </div>
    );
  }

  const tong = TONG_TOKEN_AI[muc];
  const pct = q.used_percent ?? 0;
  const hien = hienPhanTram(pct);
  const nhanPct = tranPhanTram && hien.vuot ? '>100%' : `${soVN(pct, locale)}%`;
  const vuot = tranPhanTram ? vuotHanMuc(q.used, q.limit_value) : 0;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        {/* Số lớn là phần còn lại (ADR-0091 mục 4): token cấp thêm dùng dần qua nhiều tháng, nên "còn bao nhiêu" có
            nghĩa hơn "đã dùng bao nhiêu tháng này". */}
        <p className="min-w-0 text-content-secondary">
          <span className="text-2xl font-semibold tabular-nums text-content-primary">
            {soVN(q.remaining ?? 0, locale)}
          </span>
          <span className="text-sm tabular-nums">
            {' '}
            {t('tokenAi.tomConLaiTren', { so: soVN(q.limit_value, locale) })}
          </span>
        </p>
        <span className={cn('text-sm font-semibold tabular-nums', tong.chu)}>{nhanPct}</span>
      </div>
      <div
        role="progressbar"
        aria-label={t('hanMucToken.thanhNhan')}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={beRongThanh(pct)}
        aria-valuetext={t('hanMucToken.thanhGiaTri', {
          daDung: soVN(q.used, locale),
          hanMuc: soVN(q.limit_value, locale),
        })}
        className="h-2 w-full overflow-hidden rounded-full bg-subtle"
      >
        <div className={cn('h-full rounded-full transition-all', tong.thanh)} style={{ width: `${beRongThanh(pct)}%` }} />
      </div>
      <p className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-content-muted">
        <span className="tabular-nums">{t('tokenAi.tomDaDung', { so: soVN(q.used, locale) })}</span>
        {vuot > 0 && (
          <>
            <span aria-hidden>·</span>
            <span className="font-medium tabular-nums text-danger-700">{t('manTokenAi.vuot', { so: soVN(vuot, locale) })}</span>
          </>
        )}
        <span aria-hidden>·</span>
        <span>{t('tokenAi.tomVeKhong', { ngay })}</span>
      </p>
      <DongVaoRa q={q} />
    </div>
  );
}
